/**
 * The real transport: a joined room, a star of peers, and two channels each.
 *
 * By the time this file has a room, the hard parts are settled. Roles were decided in the
 * handshake, so every peer announced here belongs in the session and a guest sees exactly one of
 * them. What is left is bookkeeping: open the unreliable channel per peer, route sends to the right
 * channel, decode what arrives, and keep the lifecycle honest when somebody leaves.
 *
 * A host that loses a collision retries once with a fresh code. A guest never does: the code came
 * from a person, and quietly joining a different one would be worse than saying nobody is there.
 */

import { DEFAULT_FALLBACK_BUDGET } from '@/net/signaling/strategyFallback';
import { DEFAULT_HOST_CLAIM_MS, joinRoomWithFallback, leaveQuietly } from '@/net/session/roomJoin';
import { IDLE_STATUS, nextStatus } from '@/net/session/connectionLifecycle';
import { NetError, asNetError } from '@/net/session/sessionErrors';
import { SIGNALING_STRATEGIES } from '@/net/signaling/signalingStrategyRegistry';
import { decodeFrame, encodeFrame } from '@/net/wire/frameCodec';
import { formatJoinCode, generateJoinCode, parseJoinCode } from '@/net/session/joinCode';
import { createPeerHeartbeat } from '@/net/session/peerHeartbeat';
import { openSnapshotChannel } from '@/net/rtc/snapshotChannel';
import { watchCandidates } from '@/net/rtc/candidateReport';
import { watchPeerHealth } from '@/net/rtc/peerHealth';
import { MESSAGE_KINDS } from '@/net/wire/messageKinds';
import type { CandidateWatch } from '@/net/rtc/candidateReport';
import type { Channel, ConnectionStatus, PeerId } from '@/net/transportContract';
import type { FallbackBudget, StrategyAttempt } from '@/net/signaling/strategyFallback';
import type { LifecycleSignal } from '@/net/session/connectionLifecycle';
import type { MessageAction } from 'trystero';
import type { MessageKind } from '@/net/wire/messageKinds';
import type { PeerHeartbeat } from '@/net/session/peerHeartbeat';
import type { PeerHealthWatch } from '@/net/rtc/peerHealth';
import type { SnapshotChannel } from '@/net/rtc/snapshotChannel';
import type { SignalingStrategy } from '@/net/signaling/signalingContract';
import type { Transport, TransportOpenOptions, TransportPeer } from '@/net/transportContract';

export interface TrysteroTransportDeps {
  readonly strategies: readonly SignalingStrategy[];
  readonly budget: FallbackBudget;
  readonly hostClaimMs: number;
}

const DEFAULTS: TrysteroTransportDeps = {
  strategies: SIGNALING_STRATEGIES,
  budget: DEFAULT_FALLBACK_BUDGET,
  hostClaimMs: DEFAULT_HOST_CLAIM_MS,
};

/** The Trystero action every reliable frame rides. One namespace: the framing carries the rest. */
const FRAME_ACTION = 'frame';

export async function openTrysteroTransport(
  options: TransportOpenOptions,
  deps: Partial<TrysteroTransportDeps> = {},
): Promise<Transport> {
  const settings = { ...DEFAULTS, ...deps };
  const code = resolveCode(options);

  try {
    return await openWithCode(options, settings, code);
  } catch (cause) {
    const error = asNetError(cause, 'peerConnectionFailed');
    // Losing a collision on a code nobody chose is worth exactly one silent retry.
    if (error.code === 'codeAlreadyHosted' && options.role === 'host' && options.code === null) {
      return openWithCode(options, settings, generateJoinCode());
    }
    throw error;
  }
}

function resolveCode(options: TransportOpenOptions): string {
  if (options.role === 'host' && options.code === null) return generateJoinCode();

  const parsed = parseJoinCode(options.code ?? '');
  if (!parsed.ok) throw new NetError('invalidCode', parsed.detail);
  return parsed.canonical;
}

async function openWithCode(
  options: TransportOpenOptions,
  settings: TrysteroTransportDeps,
  code: string,
): Promise<Transport> {
  const { listeners } = options;
  const closing = new AbortController();
  const attempts: StrategyAttempt[] = [];
  let status: ConnectionStatus = nextStatus(IDLE_STATUS, { kind: 'joinStarted' });
  listeners.onStatus(status);

  const announce = (signal: LifecycleSignal): void => {
    const updated = nextStatus(status, signal);
    if (updated === status) return;
    status = updated;
    listeners.onStatus(status);
  };

  const joined = await joinRoomWithFallback(
    {
      role: options.role,
      code,
      strategies: settings.strategies,
      budget: settings.budget,
      hostClaimMs: settings.hostClaimMs,
      heldTokens: options.heldTokens ?? ((): readonly string[] => []),
      rejoinToken: options.rejoinToken ?? ((): string | null => null),
      onAttempt: (attempt: StrategyAttempt): void => {
        attempts.push(attempt);
        announce({
          kind: 'strategyAttempt',
          strategyId: attempt.strategyId,
          detail: attemptDetail(attempt),
        });
      },
      onIssue: (error: NetError): void => {
        listeners.onError(error);
      },
      onLateFailure: (error: NetError): void => {
        lateFailure(error);
      },
    },
    closing.signal,
  ).catch((cause: unknown) => {
    throw explain(asNetError(cause, 'peerConnectionFailed'), options.role, attempts);
  });

  const { room, state } = joined;
  announce({ kind: 'roomSettled', strategyId: joined.strategyId });

  /**
   * A failure that arrives after the lobby said it was connected. A host that loses a late
   * collision has to say so and stop, rather than keep offering a code somebody else owns.
   */
  function lateFailure(error: NetError): void {
    if (status.phase === 'failed' || status.phase === 'closed') return;
    announce({ kind: 'fatal', error });
    listeners.onError(error);
    void leaveQuietly(room);
  }

  const channels = new Map<PeerId, SnapshotChannel>();
  const watches = new Map<PeerId, CandidateWatch>();
  const health = new Map<PeerId, PeerHealthWatch>();
  const peers = new Map<PeerId, TransportPeer>();
  const action: MessageAction<ArrayBuffer> = room.makeAction<ArrayBuffer>(FRAME_ACTION);

  const updatePeer = (id: PeerId, changes: Partial<TransportPeer>): void => {
    const current = peers.get(id);
    if (current === undefined) return;
    const updated = { ...current, ...changes };
    peers.set(id, updated);
    listeners.onPeerUpdate(updated);
  };

  const sendReliably = (to: PeerId, kind: MessageKind, payload: Uint8Array): void => {
    void action.send(encodeFrame(kind, payload).buffer, { target: to });
  };

  /**
   * The heartbeat, which is the only thing here that asks a peer a question.
   *
   * Everything else in this file observes: a channel opening, a connection state changing, a frame
   * arriving. None of that can see a peer whose page is still connected and no longer running, so
   * one is asked outright once a second. See [peerHeartbeat](./session/peerHeartbeat.ts).
   */
  const heartbeat: PeerHeartbeat = createPeerHeartbeat({
    nowMs: (): number => performance.now(),
    repeat: (everyMs: number, run: () => void): (() => void) => {
      const timer = setInterval(run, everyMs);
      return (): void => {
        clearInterval(timer);
      };
    },
    send: sendReliably,
    onSilent: (peerId: PeerId, reason: string): void => {
      losePeer(peerId, reason);
    },
    // Nothing to announce: a round trip is read off `rttOf` by whoever wants one, so a number that
    // changes every second does not redraw a lobby every second.
    onRtt: (): void => undefined,
  });

  const deliver = (from: PeerId, bytes: Uint8Array, channel: Channel): void => {
    try {
      const frame = decodeFrame(bytes);
      // Answered and swallowed here. A heartbeat is about the connection rather than about the
      // game, and passing it up would make every listener learn to ignore two kinds.
      if (frame.kind === MESSAGE_KINDS.ping) {
        heartbeat.receivePing(from, frame.payload);
        return;
      }
      if (frame.kind === MESSAGE_KINDS.pong) {
        heartbeat.receivePong(from, frame.payload);
        return;
      }
      listeners.onMessage(from, frame, channel);
    } catch (cause) {
      listeners.onError(asNetError(cause, 'malformedFrame'));
    }
  };

  action.onMessage = (data: ArrayBuffer, context): void => {
    deliver(context.peerId, new Uint8Array(data), 'event');
  };

  room.onPeerJoin = (peerId: string): void => {
    const role = state.peerRoles.get(peerId);
    // Only handshakes this side accepted reach here, so an unknown peer is a library surprise
    // rather than a stranger, and ignoring it keeps the star a star either way.
    if (role === undefined) return;

    const peer: TransportPeer = {
      id: peerId,
      role,
      snapshotChannelReady: false,
      relayed: null,
    };
    peers.set(peerId, peer);
    heartbeat.addPeer(peerId);
    listeners.onPeerJoin(peer);
    announce({ kind: 'peerReady', peerCount: peers.size });

    const connection = room.getPeers()[peerId];
    if (connection === undefined) return;

    channels.set(
      peerId,
      openSnapshotChannel(connection, {
        onReady: (): void => {
          updatePeer(peerId, { snapshotChannelReady: true });
        },
        onUnavailable: (): void => {
          updatePeer(peerId, { snapshotChannelReady: false });
        },
        onMessage: (bytes: ArrayBuffer): void => {
          deliver(peerId, new Uint8Array(bytes), 'snapshot');
        },
      }),
    );

    watches.set(
      peerId,
      watchCandidates(connection, (report): void => {
        updatePeer(peerId, { relayed: report.relayed });
      }),
    );

    // Trystero reports a peer that says goodbye. This reports the laptop that shut its lid.
    health.set(
      peerId,
      watchPeerHealth(connection, {
        onUnstable: (): void => {
          announce({ kind: 'peerUnstable' });
        },
        onStable: (): void => {
          announce({ kind: 'peerStable' });
        },
        onLost: (reason: string): void => {
          losePeer(peerId, reason);
        },
      }),
    );
  };

  room.onPeerLeave = (peerId: string): void => {
    losePeer(peerId, 'the peer left');
  };

  /** One exit for a peer, however it went: a polite goodbye or a connection that died. */
  function losePeer(peerId: PeerId, reason: string): void {
    const peer = peers.get(peerId);
    dropPeer(peerId);
    if (peer === undefined) return;

    listeners.onPeerLeave(peerId, reason);
    announce({
      kind: 'peerLost',
      peerCount: peers.size,
      wasHost: peer.role === 'host',
    });
  }

  function dropPeer(peerId: PeerId): void {
    heartbeat.removePeer(peerId);
    channels.get(peerId)?.close();
    channels.delete(peerId);
    watches.get(peerId)?.stop();
    watches.delete(peerId);
    health.get(peerId)?.stop();
    health.delete(peerId);
    peers.delete(peerId);
    if (state.hostPeerId === peerId) state.hostPeerId = null;
    state.peerRoles.delete(peerId);
  }

  return {
    selfId: joined.selfId,
    role: options.role,
    joinCode: formatJoinCode(code),

    status: (): ConnectionStatus => status,

    peers: (): readonly TransportPeer[] => [...peers.values()],

    rttOf: (peerId: PeerId): number | null => heartbeat.rttOf(peerId),

    send(channel: Channel, kind: MessageKind, payload: Uint8Array, target?: PeerId): void {
      const frame = encodeFrame(kind, payload);
      const recipients = target === undefined ? [...peers.keys()] : [target];

      for (const id of recipients) {
        if (!peers.has(id)) continue;
        // Reliable unless the peer has a working unreliable channel and the message wants it.
        // A snapshot that has to go reliably is late rather than lost, and the surfaces say so.
        if (channel === 'snapshot' && channels.get(id)?.send(frame) === true) continue;
        void action.send(frame.buffer, { target: id });
      }
    },

    async close(reason: string): Promise<void> {
      closing.abort(new NetError('sessionClosed', reason));
      heartbeat.stop();
      room.onPeerJoin = null;
      room.onPeerLeave = null;
      for (const peerId of [...peers.keys()]) dropPeer(peerId);
      await leaveQuietly(room);
      announce({ kind: 'closed' });
    },
  };
}

function attemptDetail(attempt: StrategyAttempt): string {
  if (attempt.outcome === 'joined') return 'Connected.';
  return `Could not connect over ${attempt.strategyId}, trying the next relay network.`;
}

/**
 * Turns "every strategy was exhausted" into what actually happened. Relays that answered and then
 * produced no host mean nobody is hosting the code, which is a different sentence from a relay
 * network that could not be reached at all.
 */
function explain(
  error: NetError,
  role: TransportOpenOptions['role'],
  attempts: readonly StrategyAttempt[],
): NetError {
  if (error.code !== 'signalingExhausted' || role !== 'guest') return error;
  const reachedARelay = attempts.some((attempt) => attempt.outcome !== 'unreachable');
  return reachedARelay ? new NetError('noHostForCode', error.detail) : error;
}
